// Library Imports
import { RootState } from 'store';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Radio, Tooltip, Typography } from 'antd';
import { useState, useEffect, useRef } from 'react';
// Local Imports
import { IListData } from '../interfaces';
import { DEFAULT_CAMPAIGN_IMAGE } from 'constants/types';
import Checkbox from 'modules/Common/Components/Checkbox';
import { getQueryWorkflowId } from 'modules/EmailBrowser/utils';
import { ListFooterIconDiv } from 'modules/Common/Components/ListCardFooterGlobal';
// Icon Imports
import verticleLine from 'assets/img/icons/verticleLine.png';
import TotalRecipientIcon from '../../../assets/svgs/TotalRecipientIcon';

const { Text } = Typography;

type Props = {
  id: any;
  listName: string;
  list?: IListData;
  thumbnailFirst: string;
  thumbnailSecond: string;
  thumbnailThird: string;
  thumbnailForth: string;
  createdBy: string;
  lastUsedBy: string;
  fromModal?: boolean;
  isActionNode?: boolean;
  totalContacts?: number;
  isFormValid?: () => any;
  enableSaveButton?: (val: boolean) => void;
  isActionModal?: boolean;
  selectedListId?: number;
  loadingListUpdate?: boolean;
  checkboxHandler?: (val: boolean, list: IListData) => void;
};

const ImageHero = styled.img`
  border-bottom: 2px solid #fff;
  height: 100%;
  width: 100%;
`;

const TileCardPhotoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 70px;
  width: 80px;
  min-width: 80px;
  .first-layer {
    height: 50%;
    display: flex;
    flex-direction: row;
    img {
      width: 50%;
      &:nth-child(1) {
        border-top-left-radius: 8px;
        border-right: 1px solid #fff;
      }
      &:nth-child(2) {
        border-top-right-radius: 8px;
        border-left: 1px solid #fff;
      }
    }
  }
  .second-layer {
    height: 50%;
    display: flex;
    flex-direction: row;
    img {
      width: 50%;
      &:nth-child(1) {
        border-bottom-left-radius: 8px;
        border-right: 1px solid #fff;
      }
      &:nth-child(2) {
        border-bottom-right-radius: 8px;
        border-left: 1px solid #fff;
      }
    }
  }
`;

const ListItem = (props: Props) => {
  // Props
  const {
    id,
    list,
    listName,
    fromModal,
    createdBy,
    lastUsedBy,
    isFormValid,
    isActionNode,
    totalContacts,
    thumbnailForth,
    thumbnailFirst,
    thumbnailThird,
    selectedListId,
    checkboxHandler,
    thumbnailSecond,
  } = props;

  // Redux States
  const selectedListsIds: number[] = useSelector((state: RootState) => state.automation.lists.selectedListIds);

  // Local States
  const [ellipsis, setEllipsis] = useState(true);
  const tileImageRef = useRef<HTMLDivElement>(null);
  const [createdByEllipsisState, setCreatedByEllipsisState] = useState(false);
  const [lastUsedByEllipsisState, setLastUsedByEllipsisState] = useState(false);

  // Local Variables
  const history = useHistory();
  const wfId = getQueryWorkflowId();
  const listThumbnails = [thumbnailFirst, thumbnailSecond, thumbnailThird, thumbnailForth];

  useEffect(() => {
    // if view details opened and user select another email, it should collapse the current item
    if (selectedListId === id) {
      isFormValid && isFormValid();
    }
  }, [selectedListId]);

  return (
    <WrapperOuter>
      <Wrapper
        className="d-flex justify-content-between"
        onClick={() =>
          history.push(
            `/audiences/view/?list_id=${id}&from=${fromModal ? 'listEdit' : 'triggerListSelection'}${fromModal ? `&workflow_id=${wfId}` : ''}`,
          )
        }
      >
        <div className="d-flex">
          <div className="checkbox align-self-center" onClick={(e) => e.stopPropagation()}>
            {isActionNode ? (
              <Checkbox
                checked={selectedListsIds?.includes(id)}
                onChange={(e: any) => checkboxHandler && list && checkboxHandler(e.target.checked, list)}
              />
            ) : (
              <Radio value={id} />
            )}
          </div>
          <TileCardPhotoWrapper onClick={() => {}}>
            <div className="first-layer" ref={tileImageRef}>
              {listThumbnails?.splice(2).map((img, index) => (
                <ImageHero src={img ?? DEFAULT_CAMPAIGN_IMAGE} alt="" className="hero-image" />
              ))}
            </div>
            <div className="second-layer">
              {listThumbnails?.splice(0, 2).map((img, index) => (
                <ImageHero src={img} alt="" className="digest-image" />
              ))}
            </div>
          </TileCardPhotoWrapper>
          <div className="content-area d-flex flex-column align-items-start align-self-center">
            <div className="subject">
              <Text className="camp-name" ellipsis={ellipsis ? { tooltip: `${listName}` } : false}>
                {listName}
              </Text>
            </div>
            <div className="d-flex">
              {createdBy && createdBy.length > 0 && (
                <div className="sendfrom">
                  Created by:
                  <Tooltip placement="top" className="tooltip-createdBy" title={createdByEllipsisState ? createdBy : undefined}>
                    <Text
                      style={{ display: 'inline-block', paddingLeft: 4 }}
                      ellipsis={{
                        onEllipsis: (ellipsis) => setCreatedByEllipsisState(ellipsis),
                      }}
                    >
                      {createdBy}
                    </Text>
                  </Tooltip>
                </div>
              )}
              {lastUsedBy && lastUsedBy.length > 0 && (
                <div className="divider">
                  <img src={verticleLine} alt="" />
                </div>
              )}
              {lastUsedBy && lastUsedBy.length > 0 && (
                <div className="sendfrom">
                  Last Used by:
                  <Tooltip placement="top" title={lastUsedByEllipsisState ? lastUsedBy : undefined}>
                    <Text
                      style={{ width: 160, display: 'inline-block', paddingLeft: 4 }}
                      ellipsis={{
                        onEllipsis: (ellipsis) => setLastUsedByEllipsisState(ellipsis),
                      }}
                    >
                      {lastUsedBy}
                    </Text>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Total Contacts */}
        <div className="contacts-icon d-flex align-items-center">
          {totalContacts !== undefined && <ListFooterIconDiv icon={<TotalRecipientIcon />} views={totalContacts} tooltipText="Total Contacts" />}
        </div>
      </Wrapper>
    </WrapperOuter>
  );
};

export default ListItem;

const WrapperOuter = styled.div<{ error?: boolean }>`
  border-radius: 6px;
  box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.08);
  background-color: #fff;
  border: ${(props) => (props?.error ? '1px solid #ff0000' : '1px solid #fff')};
  margin: 0 16px 8px 16px;
  margin: 0 16px 8px 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    border-radius: 5px;
  }
  &.invalid {
    border: 1px solid #ff0000;
  }
`;

const Wrapper = styled.div`
  background-color: #fff;
  border-radius: 7px;
  padding: 12px 16px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    border-radius: 5px;
    padding: 8px 10px;
  }
  :hover {
    cursor: pointer;
  }
  .thumbnail {
    width: 78px;
    height: 65px;
    display: block;
    border-radius: 6px;
    background-repeat: no-repeat;
    background-size: cover;
    margin: 0 13px;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin: 0 7px;
      width: 58px;
      height: 50px;
      border-radius: 4px;
    }
  }
  .content-area {
    width: calc(100% - 265px);
    margin-left: 16px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      width: calc(100% - 205px);
    }
    .name {
      color: #67696a;
      font-size: 10px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      padding-right: 70px;
    }
    .subject {
      color: #67696a;
      font-size: 16px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 13px;
      }
      .camp-name {
        max-width: 300px;
        margin-bottom: 0;
        color: #67696a;
        font-family: 'Roboto';
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: 15px;
        letter-spacing: -0.333px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          max-width: 400px;
        }
      }
    }
    .sendfrom {
      color: #67696a;
      font-family: 'Roboto';
      font-size: 10px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
      letter-spacing: -0.231px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 7px;
      display: flex;
      .ant-typography-ellipsis {
        @media all and (max-width: 1080px) {
          width: auto !important;
          max-width: 129px !important;
        }
      }
      span {
        color: #67696a;
        font-family: 'Roboto';
        font-size: 10px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        letter-spacing: -0.231px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
      }
    }
    .divider {
      display: flex;
      align-items: flex-end;
      height: 18px;
      padding: 0 5px;
    }
  }
  .date {
    margin-left: 15px;
    padding-top: 4px;
    .date-label {
      font-size: 11px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      text-align: right;
      margin-bottom: 4px;
      line-height: 12px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
      &.sended {
        position: relative;
        padding: 0;
        color: #1ec252;
        background: transparent;
        text-decoration-line: none;
        font-size: 12px;
        line-height: 12px;
        letter-spacing: -0.275px;
        .eye-view {
          width: 25px;
          height: 16px;
          position: absolute;
          top: -4px;
          left: -40px;
          background-color: #fff;
          border-radius: 22px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          padding-right: 7px;
          min-width: 65px;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 12px;
          line-height: 1;
          letter-spacing: -0.275px;
          color: #1ec252;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            left: -50px;
          }
          img {
            width: 14px;
            height: 14px;
          }
        }
        .edit-btn,
        .edit-sender-detail {
          color: #1ec252;
        }
      }
      &.scheduled {
        color: #eb7719;
      }
    }
    span {
      color: #67696a;
      font-size: 10px;
      font-weight: 400;
      line-height: 13px;
      font-family: 'Roboto', sans-serif;
      text-align: right;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 9px;
      }
    }
  }
  .contacts-icon {
    margin-right: 14px;
  }
`;
